import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ArrowLeft, Plus, Pencil, Trash2, Megaphone, Send,
  LayoutGrid, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_ROLES = ['super_admin', 'manager', 'commercial', 'coach'];
const COLORS = ['#0A84FF', '#30D158', '#FF453A', '#FFD60A', '#64D2FF', '#BF5AF2', '#FF9F0A', '#AC8E68'];

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function AdminPage({ onBack }) {
  const { authHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('apps');

  // Apps state
  const [apps, setApps] = useState([]);
  const [appDialog, setAppDialog] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [appForm, setAppForm] = useState({
    name: '', slug: '', url: '', description: '', color: '#0A84FF',
    allowed_roles: [], sort_order: 0, is_active: true,
  });

  // Confirm delete
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Announcement state
  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [announcementType, setAnnouncementType] = useState('info');
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    await Promise.all([loadApps(), loadAnnouncement()]);
  };

  // --- Apps CRUD via Supabase ---

  const loadApps = async () => {
    const { data, error } = await supabase
      .from('transform_apps')
      .select('*')
      .order('sort_order');
    if (error) {
      toast.error('Failed to load apps');
      console.error(error);
    } else {
      setApps(data || []);
    }
  };

  const openAppDialog = (app = null) => {
    if (app) {
      setEditingApp(app);
      setAppForm({
        name: app.name || '',
        slug: app.slug || '',
        url: app.url || '',
        description: app.description || '',
        color: app.color || '#0A84FF',
        allowed_roles: app.allowed_roles || [],
        sort_order: app.sort_order ?? 0,
        is_active: app.is_active !== false,
      });
    } else {
      setEditingApp(null);
      setAppForm({
        name: '', slug: '', url: '', description: '', color: '#0A84FF',
        allowed_roles: ['super_admin'], sort_order: apps.length, is_active: true,
      });
    }
    setAppDialog(true);
  };

  const saveApp = async () => {
    if (!appForm.name.trim() || !appForm.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    const payload = {
      ...appForm,
      slug: appForm.slug || slugify(appForm.name),
    };

    if (editingApp) {
      const { error } = await supabase
        .from('transform_apps')
        .update(payload)
        .eq('id', editingApp.id);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success('App updated');
    } else {
      const { error } = await supabase
        .from('transform_apps')
        .insert([payload]);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success('App created');
    }
    setAppDialog(false);
    loadApps();
  };

  const deleteApp = async (id) => {
    const { error } = await supabase
      .from('transform_apps')
      .delete()
      .eq('id', id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('App deleted');
    loadApps();
  };

  const requestDeleteApp = (app) => {
    setConfirmDelete({ type: 'app', id: app.id, name: app.name });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.type === 'app') await deleteApp(confirmDelete.id);
    setConfirmDelete(null);
  };

  // --- Roles multi-select toggle ---

  const toggleRole = (role) => {
    setAppForm(prev => ({
      ...prev,
      allowed_roles: prev.allowed_roles.includes(role)
        ? prev.allowed_roles.filter(r => r !== role)
        : [...prev.allowed_roles, role]
    }));
  };

  // --- Auto-generate slug from name ---

  const handleNameChange = (name) => {
    setAppForm(prev => ({
      ...prev,
      name,
      slug: editingApp ? prev.slug : slugify(name),
    }));
  };

  // --- Announcement ---

  const loadAnnouncement = async () => {
    try {
      const res = await axios.get(`${API}/announcements`, authHeaders());
      if (res.data?.message) {
        setCurrentAnnouncement(res.data);
        setAnnouncementMsg(res.data.message);
        setAnnouncementType(res.data.type);
      } else {
        setCurrentAnnouncement(null);
      }
    } catch {
      // silent
    }
  };

  const publishAnnouncement = async () => {
    if (!announcementMsg.trim()) return;
    try {
      await axios.post(`${API}/announcements`, { message: announcementMsg, type: announcementType }, authHeaders());
      toast.success('Announcement published');
      loadAnnouncement();
    } catch {
      toast.error('Failed to publish');
    }
  };

  const dismissAnnouncement = async () => {
    try {
      await axios.delete(`${API}/announcements`, authHeaders());
      setCurrentAnnouncement(null);
      setAnnouncementMsg('');
      toast.success('Announcement removed');
    } catch {
      toast.error('Failed to dismiss');
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="admin-back-button"
              onClick={onBack}
              variant="ghost"
              className="text-white/60 hover:text-white hover:bg-white/5 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Hub
            </Button>
            <div className="w-px h-6 bg-white/10" />
            <h1 className="text-lg font-semibold text-white tracking-tight heading-tight">Admin Panel</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fade-in">
          <TabsList className="bg-[#1C1C1E] border border-white/10 p-1 rounded-xl mb-8">
            <TabsTrigger
              value="apps"
              data-testid="admin-tab-apps"
              className="data-[state=active]:bg-[#0A84FF] data-[state=active]:text-white text-white/60 rounded-lg px-4 py-2"
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Apps
            </TabsTrigger>
            <TabsTrigger
              value="announcement"
              data-testid="admin-tab-announcement"
              className="data-[state=active]:bg-[#0A84FF] data-[state=active]:text-white text-white/60 rounded-lg px-4 py-2"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Announcement
            </TabsTrigger>
          </TabsList>

          {/* === Apps Tab === */}
          <TabsContent value="apps">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-white heading-tight">Apps</h2>
                <p className="text-sm text-white/40 mt-1">{apps.length} apps configured</p>
              </div>
              <Button
                data-testid="add-app-button"
                onClick={() => openAppDialog()}
                className="bg-[#0A84FF] hover:opacity-85 text-white rounded-lg transition-opacity duration-150 gap-2"
              >
                <Plus className="w-4 h-4" />
                Add App
              </Button>
            </div>

            <div className="bg-[#1C1C1E] border border-white/10 rounded-xl overflow-hidden">
              {apps.length === 0 ? (
                <div className="text-center py-16">
                  <LayoutGrid className="w-8 h-8 text-white/20 mx-auto mb-3" />
                  <p className="text-white/40 text-sm">No apps yet -- add your first one</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-5 py-3 tag-label text-white/40" style={{ fontSize: '10px' }}>NAME</th>
                      <th className="text-left px-5 py-3 tag-label text-white/40 hidden md:table-cell" style={{ fontSize: '10px' }}>URL</th>
                      <th className="text-left px-5 py-3 tag-label text-white/40 hidden sm:table-cell" style={{ fontSize: '10px' }}>ROLES</th>
                      <th className="text-left px-5 py-3 tag-label text-white/40 hidden lg:table-cell" style={{ fontSize: '10px' }}>ORDER</th>
                      <th className="text-left px-5 py-3 tag-label text-white/40" style={{ fontSize: '10px' }}>STATUS</th>
                      <th className="text-right px-5 py-3 tag-label text-white/40" style={{ fontSize: '10px' }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map(app => (
                      <tr key={app.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-150" data-testid={`admin-app-row-${app.id}`}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: app.color || '#0A84FF' }} />
                            <span className="text-sm font-medium text-white">{app.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-sm text-white/40 truncate max-w-[200px] block">{app.url}</span>
                        </td>
                        <td className="px-5 py-3.5 hidden sm:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(app.allowed_roles || []).map(r => (
                              <Badge key={r} variant="secondary" className="bg-white/5 border-white/10 text-white/50 hover:bg-white/5 text-[9px] tag-label px-1.5 py-0">
                                {r}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <span className="text-sm text-white/40 kpi-number">{app.sort_order}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <Badge variant="secondary" className={`text-[9px] tag-label px-1.5 py-0 ${app.is_active ? 'bg-[#30D158]/10 border-[#30D158]/20 text-[#30D158]' : 'bg-white/5 border-white/10 text-white/30'} hover:bg-transparent`}>
                            {app.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </Badge>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              data-testid={`edit-app-${app.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => openAppDialog(app)}
                              className="h-8 w-8 text-white/40 hover:text-white hover:bg-white/5"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              data-testid={`delete-app-${app.id}`}
                              variant="ghost"
                              size="icon"
                              onClick={() => requestDeleteApp(app)}
                              className="h-8 w-8 text-white/40 hover:text-[#FF453A] hover:bg-[#FF453A]/10"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* === Announcement Tab === */}
          <TabsContent value="announcement">
            <div className="max-w-3xl">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-1">
                  <Megaphone className="w-5 h-5 text-[#0A84FF]" />
                  <h2 className="text-xl font-semibold text-white heading-tight">Team Announcement</h2>
                </div>
                <p className="text-sm text-white/40">Broadcast a message visible to all team members</p>
              </div>

              {currentAnnouncement && (
                <div className="bg-[#1C1C1E] border border-white/10 rounded-xl p-5 mb-6" data-testid="current-announcement">
                  <div className="flex items-center justify-between mb-2">
                    <span className="tag-label text-[#30D158]" style={{ fontSize: '10px' }}>ACTIVE ANNOUNCEMENT</span>
                    <Button
                      data-testid="dismiss-announcement-admin"
                      onClick={dismissAnnouncement}
                      variant="ghost"
                      className="text-[#FF453A] hover:text-[#FF453A] hover:bg-[#FF453A]/10 text-xs h-7 px-2"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                  <p className="text-white text-sm">{currentAnnouncement.message}</p>
                </div>
              )}

              <div className="bg-[#1C1C1E] border border-white/10 rounded-xl p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>MESSAGE</label>
                  <Input
                    data-testid="announcement-message-input"
                    value={announcementMsg}
                    onChange={e => setAnnouncementMsg(e.target.value)}
                    className="bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                    placeholder="New tool available! Check it out..."
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>TYPE</label>
                  <Select value={announcementType} onValueChange={setAnnouncementType}>
                    <SelectTrigger data-testid="announcement-type-select" className="bg-[#2C2C2E] border-white/10 text-white rounded-lg focus:ring-1 focus:ring-[#0A84FF] w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2C2C2E] border-white/10 text-white">
                      <SelectItem value="info" className="focus:bg-white/10 focus:text-white">Info (Blue)</SelectItem>
                      <SelectItem value="success" className="focus:bg-white/10 focus:text-white">Success (Green)</SelectItem>
                      <SelectItem value="warning" className="focus:bg-white/10 focus:text-white">Warning (Yellow)</SelectItem>
                      <SelectItem value="danger" className="focus:bg-white/10 focus:text-white">Danger (Red)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="publish-announcement-button"
                  onClick={publishAnnouncement}
                  disabled={!announcementMsg.trim()}
                  className="bg-[#0A84FF] hover:opacity-85 text-white rounded-lg transition-opacity duration-150 gap-2"
                >
                  <Send className="w-4 h-4" />
                  Publish
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* App Dialog */}
      <Dialog open={appDialog} onOpenChange={setAppDialog}>
        <DialogContent className="bg-[#1C1C1E] border-white/10 text-white max-w-md" data-testid="app-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingApp ? 'Edit App' : 'Add App'}</DialogTitle>
            <DialogDescription className="text-white/40">
              {editingApp ? 'Update this app' : 'Add a new app to the hub'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>NAME *</label>
              <Input
                data-testid="app-form-name"
                value={appForm.name}
                onChange={e => handleNameChange(e.target.value)}
                className="bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="My SaaS App"
              />
            </div>
            <div className="space-y-1.5">
              <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>SLUG</label>
              <Input
                data-testid="app-form-slug"
                value={appForm.slug}
                onChange={e => setAppForm(p => ({ ...p, slug: e.target.value }))}
                className="bg-[#2C2C2E] border-white/10 text-white/50 placeholder:text-white/30 rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="my-saas-app"
              />
            </div>
            <div className="space-y-1.5">
              <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>URL *</label>
              <Input
                data-testid="app-form-url"
                value={appForm.url}
                onChange={e => setAppForm(p => ({ ...p, url: e.target.value }))}
                className="bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="https://..."
              />
            </div>
            <div className="space-y-1.5">
              <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>DESCRIPTION</label>
              <Input
                data-testid="app-form-description"
                value={appForm.description}
                onChange={e => setAppForm(p => ({ ...p, description: e.target.value }))}
                className="bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                placeholder="Brief description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>COLOR</label>
                <div className="flex flex-wrap gap-1.5 bg-[#2C2C2E] border border-white/10 rounded-lg p-2">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`app-color-${c}`}
                      onClick={() => setAppForm(p => ({ ...p, color: c }))}
                      className={`w-6 h-6 rounded-md transition-all duration-150 ${appForm.color === c ? 'ring-2 ring-white ring-offset-1 ring-offset-[#2C2C2E] scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>SORT ORDER</label>
                <Input
                  data-testid="app-form-sort-order"
                  type="number"
                  value={appForm.sort_order}
                  onChange={e => setAppForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                  className="bg-[#2C2C2E] border-white/10 text-white rounded-lg focus:ring-1 focus:ring-[#0A84FF]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>ALLOWED ROLES</label>
              <div className="flex flex-wrap gap-2">
                {ALL_ROLES.map(role => (
                  <button
                    key={role}
                    type="button"
                    data-testid={`app-role-${role}`}
                    onClick={() => toggleRole(role)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 border ${
                      appForm.allowed_roles.includes(role)
                        ? 'bg-[#0A84FF]/15 border-[#0A84FF]/40 text-[#0A84FF]'
                        : 'bg-[#2C2C2E] border-white/10 text-white/40 hover:text-white/60'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="tag-label text-white/40 block" style={{ fontSize: '10px' }}>STATUS</label>
              <button
                type="button"
                data-testid="app-form-active-toggle"
                onClick={() => setAppForm(p => ({ ...p, is_active: !p.is_active }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 border ${
                  appForm.is_active
                    ? 'bg-[#30D158]/15 border-[#30D158]/40 text-[#30D158]'
                    : 'bg-[#2C2C2E] border-white/10 text-white/40'
                }`}
              >
                {appForm.is_active ? 'Active' : 'Inactive'}
              </button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setAppDialog(false)}
              className="text-white/60 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              data-testid="app-form-save"
              onClick={saveApp}
              className="bg-[#0A84FF] hover:opacity-85 text-white transition-opacity duration-150"
            >
              {editingApp ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#1C1C1E] border-white/10" data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              Are you sure you want to delete <span className="text-white font-medium">{confirmDelete?.name}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white/60 hover:bg-white/5 hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-action"
              onClick={confirmDeleteAction}
              className="bg-[#FF453A] text-white hover:bg-[#FF453A]/80"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
